import * as Dialog from '@radix-ui/react-dialog';
import { ErrorBoundary } from '@sentry/react';
import { ReactNode, Suspense, useCallback, useEffect, useState } from 'react';

import { Button, ButtonProps } from '@/components/Button/Button';
import { RegularSpinner, RegularXmark } from '@/icons/components';
import { createScopedContext } from '@/utils/createScopedContext/createScopedContext';
import { twJoin } from '@/utils/tw-merge';

import { Warning } from '../Warning';
import { useManagedModalContext } from './ModalManager';
import { ModalStateProps } from './useModalState';

export type ModalProps = {
  children: ReactNode;
  title: string | ReactNode;
  description?: string | ReactNode;
  size?: 'medium' | 'large' | 'xlarge';
  customWidth?: `max-w-${string}`;
  afterLeave?: () => void;
  forceClose?: boolean;
  isClosingDisabled?: boolean;
  isLoading?: boolean;
  defaultOpen?: boolean;
  addContentPadding?: boolean;
  beforeClose?: () => void;
  modalStateProps?: ModalStateProps;
  /** Wraps the modal children in a suspense boundary and shows the loader when content is being loaded */
  suspense?: boolean;
};

const OVERLAY_OPEN_CLASS = 'data-[state=open]:animate-overlayShow';
const OVERLAY_CLOSED_CLASS = 'data-[state=closed]:animate-overlayHide';
const CONTENT_OPEN_CLASS = 'data-[state=open]:animate-contentShow';
const CONTENT_CLOSED_CLASS = 'data-[state=closed]:animate-contentHide';

const [ModalProvider, useModalContext] =
  createScopedContext<
    Pick<ModalProps, 'addContentPadding' | 'modalStateProps'>
  >();

export function Modal({
  children,
  title,
  description,
  forceClose,
  isClosingDisabled,
  isLoading,
  modalStateProps,
  beforeClose,
  afterLeave,
  addContentPadding = true,
  size = 'medium',
  customWidth,
  suspense,
}: ModalProps) {
  /**
   * When a modal is managed by the ModalManager, it will be wrapped in a ManagedModalContext.Provider
   * The provider contains the modal ID and an afterLeave function to remove the modal from the store
   */
  const managedModalContext = useManagedModalContext();

  const [state, setState] = useState<'open' | 'closing' | 'closed'>('open');

  const open = useCallback(() => setState('open'), []);
  const close = useCallback(() => setState('closing'), []);

  useEffect(() => {
    if (
      forceClose ||
      modalStateProps?.forceClose ||
      managedModalContext?.MANAGED_MODAL_PROP_FORCE_CLOSE
    ) {
      close();
    }
  }, [
    forceClose,
    modalStateProps?.forceClose,
    managedModalContext?.MANAGED_MODAL_PROP_FORCE_CLOSE,
    close,
  ]);

  return (
    <ModalProvider
      modalStateProps={modalStateProps}
      addContentPadding={addContentPadding}
    >
      <Dialog.Root
        open={state === 'open'}
        onOpenChange={(isOpen) => {
          if (isOpen) {
            open();
          } else {
            beforeClose?.();

            if (!isClosingDisabled && !modalStateProps?.isClosingDisabled) {
              close();
            }
          }
        }}
      >
        {['closing', 'open'].includes(state) && (
          <Dialog.Portal forceMount>
            <div
              data-testid="modal"
              onAnimationEnd={(e) => {
                const target = e.target as HTMLElement;

                if (
                  target.classList.contains(OVERLAY_CLOSED_CLASS) ||
                  target.classList.contains(CONTENT_CLOSED_CLASS)
                ) {
                  if (state !== 'open') {
                    setState('closed');

                    afterLeave?.();
                    managedModalContext?.MANAGED_MODAL_PROP_AFTER_LEAVE();
                  }
                }
              }}
            >
              <div className="relative z-modal">
                <Dialog.Overlay
                  className={twJoin(
                    'fixed inset-0 bg-black/20',
                    OVERLAY_OPEN_CLASS,
                    OVERLAY_CLOSED_CLASS
                  )}
                />

                {isLoading ? (
                  <DialogContentLoader
                    title={title}
                    description={description}
                  />
                ) : suspense ? (
                  <ErrorBoundary>
                    <Suspense
                      fallback={
                        <DialogContentLoader
                          title={title}
                          description={description}
                        />
                      }
                    >
                      <DialogContent
                        size={size}
                        title={title}
                        addContentPadding={addContentPadding}
                        description={description}
                        customWidth={customWidth}
                      >
                        {children}
                      </DialogContent>
                    </Suspense>
                  </ErrorBoundary>
                ) : (
                  <DialogContent
                    size={size}
                    title={title}
                    addContentPadding={addContentPadding}
                    description={description}
                    customWidth={customWidth}
                  >
                    {children}
                  </DialogContent>
                )}
              </div>
            </div>
          </Dialog.Portal>
        )}
      </Dialog.Root>
    </ModalProvider>
  );
}

const DialogContentLoader = ({
  title,
  description,
}: Pick<ModalProps, 'title' | 'description'>) => {
  const managedModalContext = useManagedModalContext();

  return (
    <Dialog.Content
      id={managedModalContext?.MANAGED_MODAL_PROP_ID}
      className={twJoin(
        'fixed left-1/2 top-1/2 flex size-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-md bg-white shadow-md focus:outline-none',
        CONTENT_OPEN_CLASS,
        CONTENT_CLOSED_CLASS
      )}
    >
      <Dialog.Title className="sr-only">{title}</Dialog.Title>

      {description && (
        <Dialog.Description className="sr-only" asChild>
          {description}
        </Dialog.Description>
      )}

      <RegularSpinner size={16} className="animate-spin" />
    </Dialog.Content>
  );
};

const DialogContent = ({
  size,
  title,
  addContentPadding,
  description,
  children,
  customWidth,
}: Pick<
  ModalProps,
  | 'size'
  | 'title'
  | 'addContentPadding'
  | 'description'
  | 'children'
  | 'customWidth'
>) => {
  const managedModalContext = useManagedModalContext();
  const { modalStateProps } = useModalContext();

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4">
      <Dialog.Content
        id={managedModalContext?.MANAGED_MODAL_PROP_ID}
        className={twJoin(
          'flex max-h-full w-full flex-col',
          'rounded-md bg-white shadow-md focus:outline-none',
          customWidth,
          !customWidth && size === 'medium' && 'max-w-[450px]',
          !customWidth && size === 'large' && 'max-w-[800px]',
          !customWidth && size === 'xlarge' && 'max-w-[1280px]',
          CONTENT_OPEN_CLASS,
          CONTENT_CLOSED_CLASS
        )}
      >
        <div className="flex shrink-0 grow-0 items-center justify-between border-b border-b-gray-300 px-3 py-2">
          <Dialog.Title className="style-h5-bold text-gray-800">
            {title}
          </Dialog.Title>
          <Dialog.Close asChild>
            <Button
              variant="tertiary"
              size="icon"
              icon={RegularXmark}
              disabled={modalStateProps?.action?.type === 'pending'}
            />
          </Dialog.Close>
        </div>

        <div
          className={twJoin(
            'grow-2 overflow-y-auto px-3',
            addContentPadding && 'pt-3'
          )}
        >
          {description && (
            <Dialog.Description className="style-body text-black" asChild>
              {description}
            </Dialog.Description>
          )}

          {children}
        </div>
      </Dialog.Content>
    </div>
  );
};

Modal.Footer = function ModalFooter({
  left,
  close,
  action: actionSlot,
}: {
  left?: ReactNode;
  close?: ReactNode;
  action?: ReactNode;
}) {
  const { modalStateProps: { action } = {}, addContentPadding } =
    useModalContext();

  return (
    <>
      {action?.type === 'error' && (
        <Warning size="large" variant="error" className="mt-2">
          <Warning.Title asChild>
            <h5>{action.title}</h5>
          </Warning.Title>
          <Warning.Description>{action.description}</Warning.Description>
        </Warning>
      )}

      <div
        className={twJoin(
          'sticky bottom-0 left-0 gap-1 border-t border-t-gray-300 bg-white py-2',
          addContentPadding && 'mt-2',
          'flex items-center justify-end'
        )}
      >
        {left && <div className="grow-2 w-full">{left}</div>}

        {close}
        {actionSlot}
      </div>
    </>
  );
};

Modal.Action = function ModalAction(props: Omit<ButtonProps, 'state'>) {
  const { modalStateProps: { action } = {} } = useModalContext();

  return (
    <Button
      state={
        action?.type === 'pending'
          ? 'loading'
          : action?.type === 'success'
            ? 'success'
            : undefined
      }
      {...props}
    />
  );
};

Modal.Close = function ModalClose({
  disabled,
  label = 'Annuleren',
}: {
  disabled?: boolean;
  label?: string;
}) {
  const { modalStateProps } = useModalContext();

  return (
    <Dialog.Close asChild>
      <Button
        variant="secondary"
        disabled={disabled ?? modalStateProps?.action?.type === 'pending'}
      >
        {label}
      </Button>
    </Dialog.Close>
  );
};
